import { Card, CardContent, Grid, Typography, IconButton, CircularProgress } from '@mui/material';
import { Timestamp } from 'firebase/firestore';
import { useRecordContext } from 'react-admin';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { firebaseApp } from '../../providers/firebase';
import { useState, useRef, useEffect } from 'react';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';

interface AssetSound {
    url: string;
    duration: number;
    length: number;
    type: string;
    createdAt: number;
}

interface UserAction {
    id: number;
    referenceId: string;
    referenceType: string;
    userId: string;
    userName: string;
    social: string;
    email: string;
    message: string;
    time: Timestamp;
    sound?: AssetSound;
}

// Audio Player Component for HandsUp sound
const SoundPlayer = ({ sound }: { sound: AssetSound }) => {
    const [audioUrl, setAudioUrl] = useState<string | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const audioRef = useRef<HTMLAudioElement | null>(null);

    useEffect(() => {
        const fetchAudioUrl = async () => {
            try {
                const storage = getStorage(firebaseApp);
                const audioRef = ref(storage, sound.url);
                const url = await getDownloadURL(audioRef);
                setAudioUrl(url);
            } catch (err) {
                console.error('Error fetching audio URL:', err);
                setError('Failed to load audio');
            } finally {
                setLoading(false);
            }
        };

        fetchAudioUrl();
    }, [sound.url]);

    const togglePlayPause = () => {
        if (!audioRef.current) return;

        if (isPlaying) {
            audioRef.current.pause();
        } else {
            audioRef.current.play();
        }
        setIsPlaying(!isPlaying);
    };

    const handleEnded = () => {
        setIsPlaying(false);
    };

    const formatDuration = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">Loading audio...</Typography>
            </div>
        );
    }

    if (error) {
        return (
            <Typography variant="body2" color="error">
                {error}
            </Typography>
        );
    }

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
            {audioUrl && (
                <audio
                    ref={audioRef}
                    src={audioUrl}
                    onEnded={handleEnded}
                    style={{ display: 'none' }}
                />
            )}
            <IconButton
                onClick={togglePlayPause}
                color="primary"
                size="small"
                sx={{
                    backgroundColor: 'primary.main',
                    color: 'white',
                    '&:hover': { backgroundColor: 'primary.dark' },
                }}
            >
                {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
            </IconButton>
            <Typography variant="body2" color="textSecondary">
                {formatDuration(sound.duration)}
            </Typography>
        </div>
    );
};

const HandsUpShow = () => {
    const record = useRecordContext<UserAction>();
    if (!record) return null;

    return (
        <Card sx={{ width: 600, margin: 'auto' }}>
            <CardContent>
                {/* Details */}
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <Typography variant="body1">
                            <strong>Name:</strong> {record.userName}
                        </Typography>
                        <Typography variant="body1">
                            <strong>Social:</strong> {record.social}
                        </Typography>
                        <Typography variant="body1">
                            <strong>Email:</strong> {record.email}
                        </Typography>
                        <Typography variant="body1">
                            <strong>User ID:</strong> {record.userId}
                        </Typography>
                        <Typography variant="body1">
                            <strong>Reference Type:</strong> {record.referenceType}
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="body1">
                            <strong>Reference ID:</strong> {record.referenceId}
                        </Typography>
                        <Typography variant="body1">
                            <strong>Time:</strong>{' '}
                            {record.time.toDate().toLocaleString()}
                        </Typography>
                        <Typography variant="body1">
                            <strong>Message:</strong> {record.message}
                        </Typography>
                    </Grid>
                </Grid>

                {/* Sound Player */}
                {record.sound && (
                    <div style={{ marginTop: 16 }}>
                        <Typography variant="body1">
                            <strong>Voice Message:</strong>
                        </Typography>
                        <SoundPlayer sound={record.sound} />
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default HandsUpShow;
